import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { api } from '../../services/api';
import { useNotification } from '../../Notifications';
import { useAuth } from '../../components/auth/AuthContext';

import * as Sentry from "@sentry/react";
import { 
  Loader2, 
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  RefreshCw
} from 'lucide-react';

// Import shared utilities (from BillingUtils.js)
import { 
  useResponsive,
  formatDate,
  safeParseFloat,
  currencyUtils,
  logError,
  logInfo,
  logUserAction,
  logPerformance,
  StatusBadge,
  Pagination,
  getNetworkErrorMessage 
} from './BillingUtils';

// Date validation constants
const SERVICE_START_DATE = '2025-01-01';

// Helper function to get current date in YYYY-MM-DD format
const getCurrentDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Helper function to validate date
const validateDate = (dateString) => {
  if (!dateString) return { valid: true };
  
  const date = new Date(dateString + 'T00:00:00');
  const minDate = new Date(SERVICE_START_DATE + 'T00:00:00');
  const maxDate = new Date(getCurrentDate() + 'T00:00:00');
  
  if (isNaN(date.getTime())) {
    return { valid: false, error: 'Invalid date format' };
  }
  
  if (date < minDate) {
    return { valid: false, error: `Date cannot be before ${SERVICE_START_DATE}` };
  }
  
  if (date > maxDate) {
    return { valid: false, error: 'Date cannot be in the future' };
  }
  
  return { valid: true };
};

// Helper function to validate date range
const validateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return { valid: true };
  
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  
  if (start > end) {
    return { valid: false, error: 'Start date must be less than or equal to end date' };
  }
  
  return { valid: true };
};

// Error Messages - Complete backend coverage
const ERROR_MESSAGES = {
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'AUTH_ERROR': 'Please log in again to continue',
  'ACCESS_DENIED': 'You do not have permission to perform this action',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later',
  'NETWORK_ERROR': 'Network connection error. Please check your connection and try again',
  'SERVER_ERROR': 'An internal error occurred. Please try again later',
  'INVALID_JSON': 'Invalid request format. Please try again.',
  'USER_NOT_FOUND': 'User profile not found. Please refresh and try again.',
  'INVALID_USER_PROFILE': 'Invalid user profile format. Please contact support.',
  'INCOMPLETE_PROFILE': 'Please complete your profile before accessing deployment billing.',
  'INVALID_DATE_FORMAT': 'Invalid date format. Use YYYY-MM-DD.',
  'INVALID_DATE_RANGE': 'Start date must be less than or equal to end date.',
  'INVALID_STATUS': 'Invalid status filter. Allowed values: active, completed, pending, hibernated.',
  'INVALID_MIN_MINUTES': 'Minimum minutes must be a positive integer.',
  'INVALID_MAX_MINUTES': 'Maximum minutes must be a positive integer.',
  'INVALID_MINUTES_RANGE': 'Minimum minutes cannot be greater than maximum minutes.',
  'INVALID_MIN_COST': 'Minimum cost must be a positive number.',
  'INVALID_MAX_COST': 'Maximum cost must be a positive number.',
  'INVALID_COST_RANGE': 'Minimum cost must be less than or equal to maximum cost.',
  'CURRENCY_NOT_SET': 'Please set your preferred currency in account settings.',
  'QUERY_PARAMETER_ERROR': 'Invalid query parameters. Please try again.',
  // Network Errors
'OFFLINE': "You're offline. Check your connection.",
'TIMEOUT': "Request timed out. Please try again.",
'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
'NO_RESPONSE': "No response from server. Please try again.",
'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again.",
};

// Safe error message function
// Safe error message function
const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  // Check interceptor's error field FIRST (user-friendly message from interceptor)
  if (error.error && typeof error.error === 'string') {
    return error.error;
  }
  
  // Check error code against ERROR_MESSAGES map
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  // Handle HTTP status codes (from interceptor's err.status)
  if (error.status) {
    if (error.status >= 500) {
      return ERROR_MESSAGES['SERVICE_UNAVAILABLE'];
    }
    if (error.status === 429) {
      return ERROR_MESSAGES['RATE_LIMIT_EXCEEDED'];
    }
    if (error.status === 401) {
      return ERROR_MESSAGES['AUTH_ERROR'];
    }
  }
  
  return 'Something went wrong. Please try again.';
};
// Performance: Debounce function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

const SmartFilterContainer = ({ filters, setFilters, applyFilters, resetFilters, isMobile, isTablet }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const dropdownRef = useRef(null);
  const filterBoxRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dateValidationError, setDateValidationError] = useState('');
  
  // Track temporary filter state before applying
  const [tempFilters, setTempFilters] = useState({
    duration: filters.duration,
    cost: filters.cost,
    performance: filters.performance,
    startDate: filters.startDate,
    endDate: filters.endDate
  });
  
  useEffect(() => {
    // Update tempFilters when filters change externally
    setTempFilters({
      duration: filters.duration,
      cost: filters.cost,
      performance: filters.performance,
      startDate: filters.startDate,
      endDate: filters.endDate
    });
  }, [filters]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update dropdown position when it opens
  useEffect(() => {
    if (isOpen && filterBoxRef.current) {
      const rect = filterBoxRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      });
    }
  }, [isOpen]);
  
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    
    logUserAction('smart_filter_dropdown_toggled', {
      isOpen: !isOpen,
      currentFilters: filters,
      isMobile,
      isTablet
    });
  };
  
  const toggleSection = (section) => {
    if (activeSection === section) {
      setActiveSection(null);
    } else {
      setActiveSection(section);
    }
    
    logUserAction('smart_filter_section_toggled', {
      section,
      wasActive: activeSection === section
    });
  };
  
  const handleFilterChange = (type, value) => {
    setTempFilters(prev => ({
      ...prev,
      [type]: value
    }));
    
    // Validate dates immediately when they change
    if (type === 'startDate' || type === 'endDate') {
      const newStartDate = type === 'startDate' ? value : tempFilters.startDate;
      const newEndDate = type === 'endDate' ? value : tempFilters.endDate;
      
      // Clear previous errors
      setDateValidationError('');
      
      // Validate the changed date
      const dateValidation = validateDate(value);
      if (!dateValidation.valid) {
        setDateValidationError(dateValidation.error);
        return;
      }
      
      // Validate date range if both dates are set
      if (newStartDate && newEndDate) {
        const rangeValidation = validateDateRange(newStartDate, newEndDate);
        if (!rangeValidation.valid) {
          setDateValidationError(rangeValidation.error);
          return;
        }
      }
    }
    
    logUserAction('smart_filter_temp_changed', {
      filterType: type,
      value,
      previousValue: tempFilters[type]
    });
  };
  
  const handleApplyFilters = () => {
    // Validate dates before applying
    const startDateValidation = validateDate(tempFilters.startDate);
    if (!startDateValidation.valid) {
      setDateValidationError(startDateValidation.error);
      return;
    }
    
    const endDateValidation = validateDate(tempFilters.endDate);
    if (!endDateValidation.valid) {
      setDateValidationError(endDateValidation.error);
      return;
    }
    
    const rangeValidation = validateDateRange(tempFilters.startDate, tempFilters.endDate);
    if (!rangeValidation.valid) {
      setDateValidationError(rangeValidation.error);
      return;
    }
    
    // Clear validation errors
    setDateValidationError('');
    
    // Apply the tempFilters
    setFilters(tempFilters);
    applyFilters(tempFilters, false); // false indicates not a page change
    setIsOpen(false);
    
    logUserAction('smart_filters_applied', {
      appliedFilters: tempFilters,
      previousFilters: filters
    });
  };
  
  const handleResetFilters = () => {
    resetFilters();
    setTempFilters({
      duration: '',
      cost: '',
      performance: '',
      startDate: '',
      endDate: ''
    });
    setDateValidationError('');
    setIsOpen(false);
    
    logUserAction('smart_filters_reset', {
      previousFilters: filters
    });
  };
  
  // Helper to get display text for active filters
  const getActiveFiltersText = () => {
    const activeFilters = [];
    
    if (filters.duration) {
      const durationLabels = {
        'short': 'Short Duration (<1h)',
        'medium': 'Medium Duration (1-24h)', 
        'long': 'Long Duration (>24h)'
      };
      activeFilters.push(durationLabels[filters.duration]);
    }
    
    if (filters.cost) {
      const costLabels = {
        'low': 'Low Cost (<$10)',
        'medium': 'Medium Cost ($10-$100)', 
        'high': 'High Cost (>$100)'
      };
      activeFilters.push(costLabels[filters.cost]);
    }
    
    if (filters.performance) {
      activeFilters.push(`Status: ${filters.performance}`);
    }
    
    if (filters.startDate && filters.endDate) {
      activeFilters.push(`Date: ${filters.startDate} to ${filters.endDate}`);
    } else if (filters.startDate) {
      activeFilters.push(`From: ${filters.startDate}`);
    } else if (filters.endDate) {
      activeFilters.push(`Until: ${filters.endDate}`);
    }
    
    return activeFilters.length > 0 
      ? activeFilters.join(', ') 
      : 'Filter Deployments';
  };

  return (
    <div ref={dropdownRef} style={{
      backgroundColor: '#1a1a1a',
      borderRadius: '10px',
      border: '1px solid #333333',
      padding: isMobile ? '12px' : '20px',
      paddingBottom: isMobile ? '12px' : 'calc(20px + 0.35cm)',
      width: '100%',
      position: 'relative'
    }}>
      <div style={{
        fontSize: isMobile ? '12px' : '14px',
        color: '#a0a0a0',
        fontWeight: '500',
        marginBottom: isMobile ? '8px' : '10px'
      }}>
        Deployment Filter
      </div>
      
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '8px' : '0',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center'
      }}>
        <div 
          ref={filterBoxRef}
          onClick={toggleDropdown}
          style={{
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center',
            padding: isMobile ? '12px' : '8px 12px',
            backgroundColor: '#252525',
            borderRadius: '6px',
            border: '1px solid #333333',
            width: isMobile ? '100%' : '85%'
          }}
        >
          <div style={{
            fontSize: isMobile ? '13px' : '14px',
            color: filters.duration || filters.cost || filters.performance || filters.startDate || filters.endDate 
              ? 'white' 
              : '#a0a0a0'
          }}>
            {getActiveFiltersText()}
          </div>
          <div style={{marginLeft: 'auto'}}>
            {isOpen ? (
              <ChevronUp size={isMobile ? 14 : 16} color="#a0a0a0" />
            ) : (
              <ChevronDown size={isMobile ? 14 : 16} color="#a0a0a0" />
            )}
          </div>
        </div>
        
        <button 
          onClick={handleResetFilters}
          style={{
            backgroundColor: '#333333',
            border: 'none',
            borderRadius: '50%',
            width: isMobile ? '40px' : '32px',
            height: isMobile ? '40px' : '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            marginLeft: isMobile ? '0' : '8px',
            alignSelf: isMobile ? 'center' : 'auto'
          }}
          title="Reset Filters"
        >
          <RefreshCw size={isMobile ? 18 : 16} color="white" />
        </button>
      </div>
      
      {isOpen && (
        <div style={{
          position: 'fixed',
          zIndex: 100,
          backgroundColor: '#252525',
          border: '1px solid #333333',
          borderRadius: '6px',
          padding: isMobile ? '12px' : '16px',
          width: isMobile ? '90vw' : '300px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
          top: `${dropdownPosition.top}px`,
          left: isMobile ? '5vw' : `${dropdownPosition.left}px`
        }}>
          {/* Date Validation Error */}
          {dateValidationError && (
            <div style={{
              backgroundColor: 'rgba(238, 85, 85, 0.1)',
              border: '1px solid rgba(238, 85, 85, 0.3)',
              borderRadius: '4px',
              padding: isMobile ? '8px' : '10px',
              marginBottom: isMobile ? '12px' : '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <AlertTriangle size={14} color="#ff7777" />
              <span style={{
                fontSize: isMobile ? '12px' : '13px',
                color: '#ff9999'
              }}>
                {dateValidationError}
              </span>
            </div>
          )}
          
          {/* Date Range Section */}
          <div style={{marginBottom: isMobile ? '12px' : '14.4px'}}>
            <div 
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                padding: isMobile ? '8px 0' : '7.2px 0',
                borderBottom: '1px solid #333333'
              }}
              onClick={() => toggleSection('date')}
            >
              <div style={{fontWeight: '500', fontSize: isMobile ? '14px' : '12.6px', color: 'white'}}>
                Date Range
              </div>
              <div>
                {activeSection === 'date' ? (
                  <ChevronUp size={isMobile ? 16 : 14} color="#a0a0a0" />
                ) : (
                  <ChevronDown size={isMobile ? 16 : 14} color="#a0a0a0" />
                )}
              </div>
            </div>
            
            {activeSection === 'date' && (
              <div style={{
                marginTop: isMobile ? '12px' : '10.8px', 
                display: 'flex', 
                gap: isMobile ? '8px' : '10.8px',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <div style={{flex: 1}}>
                  <div style={{fontSize: isMobile ? '12px' : '10.8px', color: '#a0a0a0', marginBottom: isMobile ? '4px' : '3.6px'}}>From</div>
                  <input 
                    type="date" 
                    value={tempFilters.startDate}
                    min={SERVICE_START_DATE}
                    max={getCurrentDate()}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    style={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #333333',
                      borderRadius: '4px',
                      padding: isMobile ? '10px' : '7.2px',
                      color: 'white',
                      width: '100%',
                      fontSize: isMobile ? '14px' : '12.6px'
                    }}
                  />
                </div>
                <div style={{flex: 1}}>
                  <div style={{fontSize: isMobile ? '12px' : '10.8px', color: '#a0a0a0', marginBottom: isMobile ? '4px' : '3.6px'}}>To</div>
                  <input 
                    type="date" 
                    value={tempFilters.endDate}
                    min={SERVICE_START_DATE}
                    max={getCurrentDate()}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    style={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #333333',
                      borderRadius: '4px',
                      padding: isMobile ? '10px' : '7.2px',
                      color: 'white',
                      width: '100%',
                      fontSize: isMobile ? '14px' : '12.6px'
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Duration Section */}
          <div style={{marginBottom: isMobile ? '14px' : '16px'}}>
            <div 
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                padding: isMobile ? '8px 0' : '8px 0',
                borderBottom: '1px solid #333333'
              }}
              onClick={() => toggleSection('duration')}
            >
              <div style={{fontWeight: '500', fontSize: isMobile ? '14px' : '14px', color: 'white'}}>
                Duration
              </div>
              <div>
                {activeSection === 'duration' ? (
                  <ChevronUp size={isMobile ? 16 : 16} color="#a0a0a0" />
                ) : (
                  <ChevronDown size={isMobile ? 16 : 16} color="#a0a0a0" />
                )}
              </div>
            </div>
            
            {activeSection === 'duration' && (
              <div style={{marginTop: isMobile ? '12px' : '12px'}}>
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '10px' : '8px',
                  marginBottom: isMobile ? '10px' : '8px'
                }}>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.duration === 'short' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.duration === 'short' ? '#e0eacc' : 'white',
                      border: tempFilters.duration === 'short' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('duration', tempFilters.duration === 'short' ? '' : 'short')}
                  >
                    Short (&lt;1h)
                  </button>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.duration === 'medium' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.duration === 'medium' ? '#e0eacc' : 'white',
                      border: tempFilters.duration === 'medium' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('duration', tempFilters.duration === 'medium' ? '' : 'medium')}
                  >
                    Medium (1-24h)
                  </button>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.duration === 'long' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.duration === 'long' ? '#e0eacc' : 'white',
                      border: tempFilters.duration === 'long' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('duration', tempFilters.duration === 'long' ? '' : 'long')}
                  >
                    Long (&gt;24h)
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Cost Section */}
          <div style={{marginBottom: isMobile ? '14px' : '16px'}}>
            <div 
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                padding: isMobile ? '8px 0' : '8px 0',
                borderBottom: '1px solid #333333'
              }}
              onClick={() => toggleSection('cost')}
            >
              <div style={{fontWeight: '500', fontSize: isMobile ? '14px' : '14px', color: 'white'}}>
                Cost
              </div>
              <div>
                {activeSection === 'cost' ? (
                  <ChevronUp size={isMobile ? 16 : 16} color="#a0a0a0" />
                ) : (
                  <ChevronDown size={isMobile ? 16 : 16} color="#a0a0a0" />
                )}
              </div>
            </div>
            
            {activeSection === 'cost' && (
              <div style={{marginTop: isMobile ? '12px' : '12px'}}>
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '10px' : '8px',
                  marginBottom: isMobile ? '10px' : '8px'
                }}>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.cost === 'low' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.cost === 'low' ? '#e0eacc' : 'white',
                      border: tempFilters.cost === 'low' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('cost', tempFilters.cost === 'low' ? '' : 'low')}
                  >
                    Low (&lt;$10)
                  </button>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.cost === 'medium' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.cost === 'medium' ? '#e0eacc' : 'white',
                      border: tempFilters.cost === 'medium' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('cost', tempFilters.cost === 'medium' ? '' : 'medium')}
                  >
                    Medium ($10-100)
                  </button>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.cost === 'high' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.cost === 'high' ? '#e0eacc' : 'white',
                      border: tempFilters.cost === 'high' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('cost', tempFilters.cost === 'high' ? '' : 'high')}
                  >
                    High (&gt;$100)
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Performance Section */}
          <div style={{marginBottom: isMobile ? '14px' : '16px'}}>
            <div 
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                padding: isMobile ? '8px 0' : '8px 0',
                borderBottom: '1px solid #333333'
              }}
              onClick={() => toggleSection('performance')}
            >
              <div style={{fontWeight: '500', fontSize: isMobile ? '14px' : '14px', color: 'white'}}>
                Status
              </div>
              <div>
                {activeSection === 'performance' ? (
                  <ChevronUp size={isMobile ? 16 : 16} color="#a0a0a0" />
                ) : (
                  <ChevronDown size={isMobile ? 16 : 16} color="#a0a0a0" />
                )}
              </div>
            </div>
            
            {activeSection === 'performance' && (
              <div style={{marginTop: isMobile ? '12px' : '12px'}}>
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '10px' : '8px',
                  marginBottom: isMobile ? '10px' : '8px'
                }}>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.performance === 'active' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.performance === 'active' ? '#e0eacc' : 'white',
                      border: tempFilters.performance === 'active' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('performance', tempFilters.performance === 'active' ? '' : 'active')}
                  >
                    Active
                  </button>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.performance === 'hibernated' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.performance === 'hibernated' ? '#e0eacc' : 'white',
                      border: tempFilters.performance === 'hibernated' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('performance', tempFilters.performance === 'hibernated' ? '' : 'hibernated')}
                  >
                    Hibernated
                  </button>
                  <button 
                    style={{
                      flex: 1,
                      padding: isMobile ? '12px' : '8px',
                      backgroundColor: tempFilters.performance === 'completed' ? '#1c2b22' : '#1a1a1a',
                      color: tempFilters.performance === 'completed' ? '#e0eacc' : 'white',
                      border: tempFilters.performance === 'completed' ? '1px solid #2a3c32' : '1px solid #333333',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilterChange('performance', tempFilters.performance === 'completed' ? '' : 'completed')}
                  >
                    Completed
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Apply Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: isMobile ? '14px' : '16px'
          }}>
            <button 
              style={{
                backgroundColor: dateValidationError ? '#666666' : '#e0eacc',
                color: dateValidationError ? '#999999' : 'black',
                border: 'none',
                borderRadius: '4px',
                padding: isMobile ? '12px 20px' : '8px 16px',
                fontWeight: '500',
                cursor: dateValidationError ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '14px' : '13px',
                width: isMobile ? '100%' : 'auto'
              }}
              onClick={handleApplyFilters}
              disabled={!!dateValidationError}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// MobileBillingCard component remains unchanged
const MobileBillingCard = ({ record, currency }) => {
  const { isMobile } = useResponsive();
  const [isExpanded, setIsExpanded] = useState(false);

  const calculateDuration = (startTime, endTime, durationMinutes) => {
    try {
      if (durationMinutes !== undefined && durationMinutes !== null) {
        const minutes = parseInt(durationMinutes, 10);
        if (isNaN(minutes)) return 'N/A';
        
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = Math.round(minutes % 60);
        return `${hours}h ${remainingMinutes}m`;
      }
      
      if (!startTime) return 'N/A';
      
      const start = new Date(startTime);
      const end = endTime ? new Date(endTime) : new Date();
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return 'N/A';
      }
      
      const diffMs = end - start;
      const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `${diffHrs}h ${diffMins}m`;
    } catch (error) {
      return 'N/A';
    }
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
    
    logUserAction('mobile_deployment_billing_card_toggled', {
      billingId: record.billing_id,
      deploymentId: record.deployment_id,
      isExpanded: !isExpanded
    });
  };

  return (
    <div style={{
      backgroundColor: '#1a1a1a',
      borderRadius: '8px',
      border: '1px solid #333333',
      padding: isMobile ? '14px' : '16px',
      marginBottom: isMobile ? '10px' : '12px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: isMobile ? '6px' : '8px'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: isMobile ? '13px' : '14px',
            color: 'white',
            fontWeight: '500',
            marginBottom: '3px'
          }}>
{record.gpu_type || 'Unknown'} x {record.gpu_count || 0}
          </div>
          <div style={{
            fontSize: isMobile ? '11px' : '12px',
            color: '#a0a0a0'
          }}>
            {formatDate(record.start_time, record.currency)}
          </div>
        </div>
        <div style={{
          textAlign: 'right',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '3px'
        }}>
          <div style={{ fontSize: isMobile ? '15px' : '16px', fontWeight: '600', color: '#e0eacc' }}>
            {currencyUtils.getSymbol(record.currency)}{safeParseFloat(record.total_cost).toFixed(2)}
          </div>
          <StatusBadge status={record.status} />
        </div>
      </div>
      
      <div style={{
        fontSize: isMobile ? '12px' : '13px',
        color: '#e0e0e0',
        marginBottom: isMobile ? '6px' : '8px',
        lineHeight: '1.3'
      }}>
        Duration: {calculateDuration(record.start_time, record.end_time, record.total_duration_minutes)}
      </div>
      
      <button
        onClick={handleToggleExpand}
        style={{
          backgroundColor: 'transparent',
          border: '1px solid #333333',
          borderRadius: '4px',
          padding: isMobile ? '8px 12px' : '6px 12px',
          color: '#a0a0a0',
          fontSize: isMobile ? '11px' : '12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          width: isMobile ? '100%' : 'auto',
          justifyContent: isMobile ? 'center' : 'flex-start'
        }}
      >
        {isExpanded ? 'Less Details' : 'More Details'}
        {isExpanded ? <ChevronUp size={isMobile ? 14 : 12} /> : <ChevronDown size={isMobile ? 14 : 12} />}
      </button>
      
      {isExpanded && (
        <div style={{
          marginTop: isMobile ? '10px' : '12px',
          paddingTop: isMobile ? '10px' : '12px',
          borderTop: '1px solid #333333'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: isMobile ? '10px' : '8px',
            fontSize: isMobile ? '11px' : '12px'
          }}>
            <div>
              <span style={{ color: '#a0a0a0' }}>Instance ID:</span>
              <div style={{ marginTop: '2px', color: 'white', wordBreak: 'break-all', textAlign: record.instance_id ? 'left' : 'center' }}>
                {record.instance_id || '-'}
              </div>
            </div>
            <div>
              <span style={{ color: '#a0a0a0' }}>Rate:</span>
              <div style={{ marginTop: '2px', color: 'white' }}>
                {currencyUtils.getSymbol(record.currency)}
                {typeof record.per_minute_rate === 'string' && record.per_minute_rate.includes('0.00750') 
                  ? "0.00750" 
                  : safeParseFloat(record.per_minute_rate).toFixed(5)}/min
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const DeploymentBillingTab = ({ barrackId }) => {
  const { isMobile, isTablet } = useResponsive();
  const notification = useNotification();
  const { handleAuthError } = useAuth();

  const [displayBillingRecords, setDisplayBillingRecords] = useState([]);
  const [currency, setCurrency] = useState(currencyUtils.defaultCurrency);
  const [summary, setSummary] = useState({
    totalMinutes: 0,
    activeCount: 0,
    hibernatedCount: 0,
    completedCount: 0,
    averageUsage: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastShownError, setLastShownError] = useState('');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    duration: '',
    cost: '',
    performance: ''
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 20
  });
  
  // Performance: Initial load tracking
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  
  // Performance: Request cancellation
  
  // Performance: Retry logic
  const maxRetries = 3;
  const retryAttempts = useRef(0);
  
  const prevFiltersRef = useRef(filters);
  const isInitialLoad = useRef(true);

  // Set Sentry context for DeploymentBillingTab
  useEffect(() => {
    Sentry.setContext("deployment-billing-tab", {
      barrackId,
      isMobile,
      isTablet,
      currentFilters: filters,
      currentPage: pagination.currentPage,
      totalItems: pagination.totalItems,
      currency
    });
  }, [barrackId, isMobile, isTablet, filters, pagination, currency]);

  // Convert errors to toast notifications
  useEffect(() => {
    if (error && error !== lastShownError) {
      notification.error(error);
      setLastShownError(error);
      setError(null);
    }
  }, [error, notification, lastShownError]);

  const clearError = useCallback(() => {
    setError(null);
    setLastShownError('');
  }, []);

  // Component mount logging
  useEffect(() => {
    logInfo('DeploymentBillingTab component mounted', {
      barrackId,
      isMobile,
      isTablet
    });
  }, []);

 

  // Performance: Debounced fetch function
  const debouncedFetchBillingRecords = useMemo(
    () => debounce(async (currentFilters = filters, page = pagination.currentPage) => {
      await fetchBillingRecords(currentFilters, page);
    }, 300),
    [filters, pagination.currentPage]
  );

  // Initial load with page 1
  useEffect(() => {
    fetchBillingRecords(filters, 1);
    isInitialLoad.current = false;
  }, [barrackId]);
  
  // Handle filter changes
  useEffect(() => {
    if (!isInitialLoad.current && JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters)) {
      logUserAction('deployment_billing_filters_changed', {
        previousFilters: prevFiltersRef.current,
        newFilters: filters
      });
      fetchBillingRecords(filters, 1);
      prevFiltersRef.current = {...filters};
    }
  }, [filters]);

  // Calculate duration helper
  const calculateDuration = (startTime, endTime, durationMinutes) => {
    try {
      if (durationMinutes !== undefined && durationMinutes !== null) {
        const minutes = parseInt(durationMinutes, 10);
        if (isNaN(minutes)) return 'N/A';
        
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = Math.round(minutes % 60);
        return `${hours}h ${remainingMinutes}m`;
      }
      
      if (!startTime) return 'N/A';
      
      const start = new Date(startTime);
      const end = endTime ? new Date(endTime) : new Date();
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return 'N/A';
      }
      
      const diffMs = end - start;
      const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `${diffHrs}h ${diffMins}m`;
    } catch (error) {
      logError('Error calculating duration', { startTime, endTime, durationMinutes }, error);
      return 'N/A';
    }
  };

// Fetch billing records from backend with server-side pagination
const fetchBillingRecords = async (currentFilters = filters, page = pagination.currentPage) => {
  const startTime = performance.now();
  
  // Check retry limit
  if (retryAttempts.current >= maxRetries) {
    setError('Maximum refresh attempts reached. Please wait before trying again.');
    logError('Maximum deployment billing fetch attempts exceeded', {
      attempts: retryAttempts.current,
      maxRetries
    });
    return;
  }
  
  retryAttempts.current++;
  setIsLoading(true);
  setError(null);
  
  try {
    const params = {
      page: page,
      limit: pagination.limit,
      barrack_id: barrackId
    };
    
    if (currentFilters.startDate) params.start_date = currentFilters.startDate;
    if (currentFilters.endDate) params.end_date = currentFilters.endDate;
    if (currentFilters.performance) params.status = currentFilters.performance;
    
    if (currentFilters.duration) {
      switch(currentFilters.duration) {
        case 'short':
          params.max_minutes = 60;
          break;
        case 'medium':
          params.min_minutes = 60;
          params.max_minutes = 1440;
          break;
        case 'long':
          params.min_minutes = 1440;
          break;
      }
    }
    
    if (currentFilters.cost) {
      switch(currentFilters.cost) {
        case 'low':
          params.max_cost = 10;
          break;
        case 'medium':
          params.min_cost = 10;
          params.max_cost = 100;
          break;
        case 'high':
          params.min_cost = 100;
          break;
      }
    }
    
    logInfo('Fetching billing records with params', { params, barrackId });
    
    const response = await api.getAggregatedDeploymentBilling(params);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response && response.success) {
      const records = response.records || [];
      
      setDisplayBillingRecords(records);
      
      setPagination(prev => ({
        ...prev,
        currentPage: response.page || page,
        totalPages: response.total_pages || 1,
        totalItems: response.total || 0
      }));
      
      if (response.summary) {
        setSummary(response.summary);
        logInfo('Using backend-calculated summary', response.summary);
      }
      
      if (response.currency) {
        setCurrency(response.currency);
      }
      
      if (!initialDataLoaded) setInitialDataLoaded(true);
      retryAttempts.current = 0; // Reset on success
      
      logPerformance('fetch_deployment_billing', duration, {
        success: true,
        recordCount: records.length,
        page: page,
        totalItems: response.total,
        barrackId
      });
      
      logInfo('Deployment billing records loaded successfully', {
        recordCount: records.length,
        page: page,
        totalItems: response.total,
        duration: `${duration.toFixed(2)}ms`
      });
    } else {
      const errorMsg = response.error || 'Failed to fetch billing records';
      throw new Error(errorMsg);
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Define network error codes for identification
    const NETWORK_ERROR_CODES = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
    
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'AUTH_ERROR') {
      handleAuthError();
      setIsLoading(false);
      return;
    }
    
    // Position 2: Network errors (no response from server)
    if (NETWORK_ERROR_CODES.includes(err.code)) {
      const errorMessage = err.error || ERROR_MESSAGES[err.code] || 'Network error. Please check your connection.';
      setError(errorMessage);
      logError('Network error in fetchBillingRecords', { 
        code: err.code, 
        online: navigator.onLine,
        duration: `${duration.toFixed(2)}ms`,
        barrackId
      }, err);
      setIsLoading(false);
      return;
    }
    
    // Position 3: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      const errorMessage = err.error || 'Too many requests. Please wait before trying again.';
      setError(errorMessage);
      logError('Deployment billing fetch rate limited', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      setIsLoading(false);
      return;
    }
    
    // Position 4: Known error codes from ERROR_MESSAGES map
    if (err.code && ERROR_MESSAGES[err.code]) {
      setError(ERROR_MESSAGES[err.code]);
      logError('Deployment billing fetch error with known code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      setIsLoading(false);
      return;
    }
    
    // Position 5: Use interceptor's error message directly
    if (err.error && typeof err.error === 'string') {
      setError(err.error);
      logError('Deployment billing fetch error with interceptor message', {
        code: err.code,
        error: err.error,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      setIsLoading(false);
      return;
    }
    
    // Position 6: Fallback
    const safeMessage = getSafeErrorMessage(err);
    setError(safeMessage);
    
    logError('Error fetching deployment billing records', {
      duration: `${duration.toFixed(2)}ms`,
      currentFilters,
      page,
      barrackId,
      error: safeMessage
    }, err);
    
    logPerformance('fetch_deployment_billing', duration, {
      success: false,
      error: err.code || 'UNKNOWN'
    });
  } finally {
    setIsLoading(false);
  }
};  // Handle filter changes
  const applyFilters = (overrideValues = {}) => {
    const updatedFilters = { ...filters, ...overrideValues };
    
    logUserAction('deployment_billing_filters_applied', {
      previousFilters: filters,
      newFilters: updatedFilters,
      overrideValues
    });
    
    setFilters(updatedFilters);
  };

  // Reset all filters
  const resetFilters = () => {
    logUserAction('deployment_billing_filters_reset_all', {
      previousFilters: filters
    });
    
    setFilters({
      startDate: '',
      endDate: '',
      duration: '',
      cost: '',
      performance: ''
    });
  };

  // Handle pagination changes by fetching from backend
  const handlePageChange = (newPage) => {
    if (newPage !== pagination.currentPage && newPage > 0 && newPage <= pagination.totalPages) {
      logUserAction('deployment_billing_page_changed', {
        previousPage: pagination.currentPage,
        newPage,
        totalPages: pagination.totalPages,
        totalItems: pagination.totalItems
      });
      
      fetchBillingRecords(filters, newPage);
    }
  };
  
  // Handle refresh with retry limit
  const handleRefresh = useCallback(async () => {
    if (retryAttempts.current >= maxRetries) {
      const errorMsg = 'Maximum refresh attempts reached. Please wait before trying again.';
      setError(errorMsg);
      
      logError('Maximum refresh attempts exceeded', {
        attempts: retryAttempts.current,
        maxRetries
      });
      
      return;
    }
    
    retryAttempts.current++;
    
    logUserAction('refresh_deployment_billing', {
      attempt: retryAttempts.current,
      maxRetries
    });
    
    logInfo('Refreshing deployment billing', {
      attempt: retryAttempts.current,
      maxRetries
    });
    
    try {
      await debouncedFetchBillingRecords();
      retryAttempts.current = 0; // Reset on success
      logInfo('Deployment billing refresh completed successfully');
    } catch (err) {
      if (retryAttempts.current >= maxRetries) {
        const safeMessage = getSafeErrorMessage(err);
        setError(safeMessage);
        
        logError('Deployment billing refresh failed after max retries', {
          attempts: retryAttempts.current,
          maxRetries
        }, err);
      }
    }
  }, [debouncedFetchBillingRecords]);
  
  // Component unmount cleanup
  useEffect(() => {
    return () => {
      logInfo('DeploymentBillingTab component unmounted', {
        barrackId,
        finalState: {
          totalRecords: pagination.totalItems,
          currentPage: pagination.currentPage,
          appliedFilters: filters
        }
      });
    };
  }, []);

  // Render desktop/tablet layout
  if (!isMobile) {
    return (
      <div>
        <div style={{
          display: 'flex',
          gap: isTablet ? '12px' : '20px',
          marginBottom: '28px',
          flexWrap: isTablet ? 'wrap' : 'nowrap'
        }}>
          {/* Total Usage Card */}
          <div style={{
            backgroundColor: '#1a1a1a',
            borderRadius: '10px',
            border: '1px solid #333333',
            padding: isTablet ? '16px' : '20px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
            width: isTablet ? 'calc(50% - 6px)' : 'calc(20% - 20px)',
            height: isTablet ? 'auto' : 'calc(100% - 3cm)'
          }}>
            <div style={{
              fontSize: '14px',
              color: '#a0a0a0',
              fontWeight: '500',
              marginBottom: '10px'
            }}>
              Total Usage
            </div>
            <div style={{
              fontSize: isTablet ? '24px' : '28px',
              fontWeight: '600',
              color: 'white'
            }}>
              {Math.floor(summary.totalMinutes / 60)}h {Math.round(summary.totalMinutes % 60)}m
            </div>
          </div>

         {/* Total Cost Card */}
<div style={{   
  backgroundColor: '#1a1a1a',   
  borderRadius: '10px',   
  border: '1px solid #333333',   
  padding: isTablet ? '16px' : '20px',   
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',   
  width: isTablet ? 'calc(50% - 6px)' : 'calc(20% - 20px)',   
  height: isTablet ? 'auto' : 'calc(100% - 3cm)',
  marginLeft: isTablet ? '0' : '.2cm'
}}>   
  <div style={{     
    fontSize: '14px',     
    color: '#a0a0a0',     
    fontWeight: '500',     
    marginBottom: '10px'   
  }}>     
    Total Cost   
  </div>   
  <div style={{     
    fontSize: isTablet ? '24px' : '28px',     
    fontWeight: '600',     
    color: '#e0eacc'   
  }}>     
    {currencyUtils.getSymbol(currency)}{safeParseFloat(summary.totalCost).toFixed(2)}   
  </div> 
</div>
          
          {/* Combined Active & Hibernated Deployments Card */}
          <div style={{
            backgroundColor: '#1a1a1a',
            borderRadius: '10px',
            border: '1px solid #333333',
            padding: isTablet ? '16px' : '20px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
            width: isTablet ? 'calc(50% - 6px)' : 'calc(20% - 20px)',
            height: isTablet ? 'auto' : 'calc(100% - 3cm)',
            marginLeft: isTablet ? '0' : '0cm'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <div>
                <div style={{
                  fontSize: '14px',
                  color: '#e0eacc',
                  marginBottom: '4px'
                }}>
                  Active:
                </div>
                <div style={{
                  fontSize: isTablet ? '22px' : '26px',
                  fontWeight: '600',
                  color: 'white'
                }}>
                  {summary.activeCount}
                </div>
              </div>
              <div>
                <div style={{
                  fontSize: '14px',
                  color: '#e0eacc',
                  marginBottom: '4px'
                }}>
                  Hibernated:
                </div>
                <div style={{
                  fontSize: isTablet ? '22px' : '26px',
                  fontWeight: '600',
                  color: 'white'
                }}>
                  {summary.hibernatedCount}
                </div>
              </div>
            </div>
          </div>
          
          {/* Smart Filter Card */}
          <div style={{
            flex: 1,
            height: isTablet ? 'auto' : 'calc(70%)',
            marginRight: isTablet ? '0' : '-5.5cm',
            width: isTablet ? 'calc(50% - 6px)' : 'auto'
          }}>
            <div style={{ width: isTablet ? '100%' : '400px' }}>
              <SmartFilterContainer 
                filters={filters}
                setFilters={setFilters}
                applyFilters={applyFilters}
                resetFilters={resetFilters}
                isMobile={isMobile}
                isTablet={isTablet}
              />
            </div>
          </div>
        </div>

        {/* Persistent Error Display with Retry */}
        {error && (
          <div style={{
            marginBottom: '24px',
            backgroundColor: 'rgba(238, 85, 85, 0.1)',
            border: '1px solid rgba(238, 85, 85, 0.3)',
            borderRadius: '8px',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
              <AlertTriangle size={20} color="#ff7777" />
              <p style={{ color: '#ff9999', fontSize: '14px', margin: 0 }}>{error}</p>
            </div>
            <button
              onClick={() => {
                setError(null);
                retryAttempts.current = 0;
                handleRefresh();
              }}
              style={{
                backgroundColor: 'rgba(238, 85, 85, 0.2)',
                border: '1px solid rgba(238, 85, 85, 0.4)',
                borderRadius: '4px',
                padding: '8px 16px',
                color: '#ff9999',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(238, 85, 85, 0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(238, 85, 85, 0.2)';
              }}
            >
              <RefreshCw size={14} />
              Retry
            </button>
          </div>
        )}

        {isLoading ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 0',
            backgroundColor: '#1a1a1a',
            borderRadius: '10px',
            border: '1px solid #333333',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
            color: '#a0a0a0',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}>
            <Loader2 className="animate-spin" size={24} />
            Loading billing records...
          </div>

        ) : (
          <>
            <div style={{
              backgroundColor: '#1a1a1a',
              borderRadius: '10px',
              border: '1px solid #333333',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
              overflowX: 'auto'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: '0'
              }}>
                <thead>
                  <tr>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Instance ID</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Start Time</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>End Time</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Duration</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>GPU Type</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Rate</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Total Cost</th>
                    <th style={{
                      backgroundColor: 'rgba(20, 20, 20, 0.8)',
                      color: '#b0b0b0',
                      fontWeight: '600',
                      textAlign: 'left',
                      padding: isTablet ? '12px 14px' : '14px 18px',
                      fontSize: isTablet ? '12px' : '13px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {displayBillingRecords.length > 0 ? (
                    displayBillingRecords.map((record) => (
                      <tr 
                        key={record.billing_id}
                        style={{
                          transition: 'background-color 0.15s ease'
                        }}
                      >
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
                          textAlign: record.instance_id ? 'left' : 'center'
                        }}>{record.instance_id || '-'}</td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>{formatDate(record.start_time, record.currency)}</td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>{record.end_time ? formatDate(record.end_time, record.currency) : 'Active'}</td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>{calculateDuration(record.start_time, record.end_time, record.total_duration_minutes)}</td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>{record.gpu_type} x {record.gpu_count}</td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>
                          {currencyUtils.getSymbol(record.currency)}
                          {typeof record.per_minute_rate === 'string' && record.per_minute_rate.includes('0.00750') 
                            ? "0.00750" 
                            : safeParseFloat(record.per_minute_rate).toFixed(5)}/min
                        </td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>
                          {currencyUtils.getSymbol(record.currency)}
                          {safeParseFloat(record.total_cost).toFixed(2)}
                        </td>
                        <td style={{
                          padding: isTablet ? '12px 14px' : '16px 18px',
                          fontSize: isTablet ? '13px' : '14px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}>
                          <StatusBadge status={record.status} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td 
                        colSpan="8" 
                        style={{
                          textAlign: 'center',
                          color: '#777777',
                          padding: '40px',
                          fontStyle: 'italic'
                        }}
                      >No billing records found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {displayBillingRecords.length > 0 && (
              <Pagination 
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                isMobile={isMobile}
              />
            )}
          </>
        )}
      </div>
    );
  }

  // Mobile layout
  return (
    <div style={{ padding: isMobile ? '12px' : '16px' }}>
      {/* Mobile Summary Cards - 2x2 Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: isMobile ? '8px' : '12px',
        marginBottom: isMobile ? '16px' : '20px'
      }}>
        {/* Total Usage */}
        <div style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          border: '1px solid #333333',
          padding: isMobile ? '12px' : '16px'
        }}>
          <div style={{
            fontSize: isMobile ? '11px' : '12px',
            color: '#a0a0a0',
            fontWeight: '500',
            marginBottom: isMobile ? '6px' : '8px'
          }}>
            Total Usage
          </div>
          <div style={{
            fontSize: isMobile ? '18px' : '20px',
            fontWeight: '600',
            color: 'white'
          }}>
            {Math.floor(summary.totalMinutes / 60)}h {Math.round(summary.totalMinutes % 60)}m
          </div>
        </div>
        
  {/* Total Cost */}
<div style={{
  backgroundColor: '#1a1a1a',
  borderRadius: '8px',
  border: '1px solid #333333',
  padding: isMobile ? '12px' : '16px'
}}>
  <div style={{
    fontSize: isMobile ? '11px' : '12px',
    color: '#a0a0a0',
    fontWeight: '500',
    marginBottom: isMobile ? '6px' : '8px'
  }}>
    Total Cost
  </div>
  <div style={{
    fontSize: isMobile ? '18px' : '20px',
    fontWeight: '600',
    color: '#e0eacc'
  }}>
    {currencyUtils.getSymbol(currency)}{safeParseFloat(summary.totalCost).toFixed(2)}
  </div>
</div>
        {/* Active Deployments */}
        <div style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          border: '1px solid #333333',
          padding: isMobile ? '12px' : '16px'
        }}>
          <div style={{
            fontSize: isMobile ? '11px' : '12px',
            color: '#a0a0a0',
            fontWeight: '500',
            marginBottom: isMobile ? '6px' : '8px'
          }}>
            Active / Hibernated
          </div>
          <div style={{
            fontSize: isMobile ? '18px' : '20px',
            fontWeight: '600',
            color: 'white'
          }}>
            {summary.activeCount} / {summary.hibernatedCount}
          </div>
        </div>

        {/* Filter container */}
        <div style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          border: '1px solid #333333',
          padding: isMobile ? '8px' : '16px'
        }}>
          <SmartFilterContainer 
            filters={filters}
            setFilters={setFilters}
            applyFilters={applyFilters}
            resetFilters={resetFilters}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        </div>
      </div>

      {/* Persistent Error Display with Retry */}
      {error && (
        <div style={{
          marginBottom: isMobile ? '16px' : '20px',
          backgroundColor: 'rgba(238, 85, 85, 0.1)',
          border: '1px solid rgba(238, 85, 85, 0.3)',
          borderRadius: '8px',
          padding: isMobile ? '12px' : '16px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: '10px',
            marginBottom: '12px'
          }}>
            <AlertTriangle size={18} color="#ff7777" style={{ flexShrink: 0, marginTop: '2px' }} />
            <p style={{ 
              color: '#ff9999', 
              fontSize: isMobile ? '13px' : '14px', 
              margin: 0,
              flex: 1,
              lineHeight: '1.4'
            }}>
              {error}
            </p>
          </div>
          <button
            onClick={() => {
              setError(null);
              retryAttempts.current = 0;
              handleRefresh();
            }}
            style={{
              backgroundColor: 'rgba(238, 85, 85, 0.2)',
              border: '1px solid rgba(238, 85, 85, 0.4)',
              borderRadius: '4px',
              padding: isMobile ? '10px 16px' : '8px 16px',
              color: '#ff9999',
              fontSize: isMobile ? '13px' : '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              width: '100%'
            }}
          >
            <RefreshCw size={14} />
            Retry
          </button>
        </div>
      )}

      {isLoading ? (
        <div style={{
          textAlign: 'center',
          padding: isMobile ? '30px 16px' : '40px 20px',
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          border: '1px solid #333333',
          color: '#a0a0a0',
          fontSize: isMobile ? '13px' : '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isMobile ? '6px' : '8px'
        }}>
          <Loader2 className="animate-spin" size={isMobile ? 18 : 20} />
          Loading billing records...
        </div>

      ) : (
        <>
          {/* Mobile Billing Cards */}
          <div>
            {displayBillingRecords.length > 0 ? (
              displayBillingRecords.map((record) => (
                <MobileBillingCard
                  key={record.billing_id}
                  record={record}
                  currency={currency}
                />
              ))
            ) : (
              <div style={{
                textAlign: 'center',
                color: '#777777',
                padding: isMobile ? '30px 16px' : '40px 20px',
                fontStyle: 'italic',
                backgroundColor: '#1a1a1a',
                borderRadius: '8px',
                border: '1px solid #333333',
                fontSize: isMobile ? '13px' : '14px'
              }}>
                No billing records found
              </div>
            )}
          </div>
          
          {displayBillingRecords.length > 0 && (
            <Pagination 
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              isMobile={isMobile}
            />
          )}
        </>
      )}
    </div>
  );
};

export default DeploymentBillingTab;